import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { DURATION_NORMAL, DURATION_SLOW } from '../utils/animationTiming';
import { useImagePreloader } from '../hooks/useImagePreloader';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from './icons';

interface PhotoGalleryProps {
  photos: string[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onFullscreen?: () => void;
  className?: string;
  // Story 6.4: Fullscreen mode props
  isFullscreen?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const SWIPE_THRESHOLD = 50; // Minimum swipe distance in pixels

// Animation variants for slide transitions - moved outside component for memoization
const slideVariants = {
  enter: (direction: 'left' | 'right') => ({
    x: direction === 'right' ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: 'left' | 'right') => ({
    x: direction === 'right' ? -50 : 50,
    opacity: 0,
  }),
};

/**
 * PhotoGallery - Story 6.2 + 6.3 + 6.4
 * Displays photos with navigation controls for multi-photo places.
 * Single-photo places show no controls or indicator.
 * Supports swipe (mobile), arrow buttons (desktop), keyboard navigation, and fullscreen mode.
 */
export function PhotoGallery({
  photos,
  currentIndex,
  onIndexChange,
  onFullscreen,
  className = '',
  isFullscreen = false,
  onFullscreenChange,
}: PhotoGalleryProps) {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const touchStartRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Story 6.5: Preload current and adjacent images
  const { loadedImages } = useImagePreloader(photos, currentIndex);

  // Issue 4: Handle empty photos array gracefully
  if (photos.length === 0) {
    return (
      <div className={`relative ${className}`} data-testid="photo-gallery">
        <div className="w-full aspect-[4/3] bg-bg-surface rounded-lg flex items-center justify-center">
          <span className="text-gray-500 text-sm">No photos</span>
        </div>
      </div>
    );
  }

  const hasMultiplePhotos = photos.length > 1;
  const currentPhoto = photos[currentIndex] || photos[0];
  // Issue 1: Use loadedImages to skip placeholder for preloaded images
  const isPreloaded = loadedImages.has(currentPhoto);
  const canGoNext = currentIndex < photos.length - 1;
  const canGoPrev = currentIndex > 0;

  const handleImageLoad = useCallback(() => {
    setIsImageLoading(false);
    setHasError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsImageLoading(false);
    setHasError(true);
  }, []);

  // Open fullscreen
  const handleOpenFullscreen = useCallback(() => {
    triggerElementRef.current = document.activeElement as HTMLElement;
    if (onFullscreenChange) {
      onFullscreenChange(true);
    } else if (onFullscreen) {
      onFullscreen();
    }
  }, [onFullscreenChange, onFullscreen]);

  // Close fullscreen
  const handleCloseFullscreen = useCallback(() => {
    if (onFullscreenChange) {
      onFullscreenChange(false);
    }
    // Return focus to trigger element
    requestAnimationFrame(() => {
      if (triggerElementRef.current && document.body.contains(triggerElementRef.current)) {
        triggerElementRef.current.focus();
      }
    });
  }, [onFullscreenChange]);

  // Navigation handlers
  const goToNext = useCallback(() => {
    if (canGoNext) {
      setSlideDirection('right');
      onIndexChange(currentIndex + 1);
    }
  }, [canGoNext, currentIndex, onIndexChange]);

  const goToPrev = useCallback(() => {
    if (canGoPrev) {
      setSlideDirection('left');
      onIndexChange(currentIndex - 1);
    }
  }, [canGoPrev, currentIndex, onIndexChange]);

  // Touch event handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartRef.current === null) return;

    const touchEnd = e.changedTouches[0].clientX;
    const deltaX = touchEnd - touchStartRef.current;

    if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
      if (deltaX < 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }

    touchStartRef.current = null;
  }, [goToNext, goToPrev]);

  // Keyboard navigation (both inline and fullscreen)
  useEffect(() => {
    const container = isFullscreen ? fullscreenRef.current : containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle ESC to close fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        handleCloseFullscreen();
        return;
      }

      // Handle arrow navigation
      if (!hasMultiplePhotos) return;

      // Only handle if focus is within this component or in fullscreen mode
      if (!isFullscreen && !container.contains(document.activeElement) && document.activeElement !== container) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasMultiplePhotos, goToNext, goToPrev, isFullscreen, handleCloseFullscreen]);

  // Focus trap for fullscreen mode
  useFocusTrap(fullscreenRef, isFullscreen);

  // Focus the fullscreen container when opened
  useEffect(() => {
    if (isFullscreen && fullscreenRef.current) {
      fullscreenRef.current.focus();
    }
  }, [isFullscreen]);

  // Reset loading state when photo changes (skip if already preloaded)
  useEffect(() => {
    setIsImageLoading(!loadedImages.has(currentPhoto));
    setHasError(false);
  }, [currentPhoto, loadedImages]);

  // Navigation arrows component (reused in inline and fullscreen)
  const NavigationArrows = ({ className: arrowClassName = '' }: { className?: string }) => (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          goToPrev();
        }}
        disabled={!canGoPrev}
        className={`absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center
          bg-black/50 rounded-full text-white transition-opacity duration-fast
          hover:bg-black/70 focus:bg-black/70 focus:outline-none focus:ring-2 focus:ring-primary
          ${canGoPrev ? 'opacity-70 hover:opacity-100' : 'opacity-30 cursor-not-allowed'}
          ${arrowClassName}`}
        aria-label="Previous photo"
        data-testid="nav-prev"
      >
        <ChevronLeftIcon />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          goToNext();
        }}
        disabled={!canGoNext}
        className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center
          bg-black/50 rounded-full text-white transition-opacity duration-fast
          hover:bg-black/70 focus:bg-black/70 focus:outline-none focus:ring-2 focus:ring-primary
          ${canGoNext ? 'opacity-70 hover:opacity-100' : 'opacity-30 cursor-not-allowed'}
          ${arrowClassName}`}
        aria-label="Next photo"
        data-testid="nav-next"
      >
        <ChevronRightIcon />
      </button>
    </>
  );

  // Photo counter component (reused in inline and fullscreen)
  const PhotoCounter = () => (
    <div
      className="absolute bottom-4 right-4 bg-black/60 px-3 py-1.5 rounded-full text-sm text-white"
      aria-live="polite"
      data-testid="photo-counter"
    >
      {currentIndex + 1}/{photos.length}
    </div>
  );

  // Fullscreen overlay component
  const FullscreenOverlay = () => {
    if (!isFullscreen) return null;

    return createPortal(
      <motion.div
        ref={fullscreenRef}
        className="fixed inset-0 z-[3000] flex items-center justify-center bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: DURATION_SLOW / 1000 }}
        onClick={handleCloseFullscreen}
        tabIndex={0}
        role="dialog"
        aria-modal="true"
        aria-label={`Fullscreen photo ${currentIndex + 1} of ${photos.length}`}
        data-testid="fullscreen-overlay"
        onTouchStart={hasMultiplePhotos ? handleTouchStart : undefined}
        onTouchEnd={hasMultiplePhotos ? handleTouchEnd : undefined}
      >
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCloseFullscreen();
          }}
          className="absolute top-4 right-4 z-10 w-11 h-11 flex items-center justify-center
            bg-black/50 rounded-full text-white
            hover:bg-black/70 focus:bg-black/70 focus:outline-none focus:ring-2 focus:ring-primary
            transition-colors duration-fast"
          aria-label="Close fullscreen"
          data-testid="fullscreen-close"
        >
          <CloseIcon className="w-6 h-6" />
        </button>

        {/* Fullscreen photo */}
        <AnimatePresence mode="wait" custom={slideDirection}>
          <motion.img
            key={currentPhoto}
            src={currentPhoto}
            alt={`Photo ${currentIndex + 1} of ${photos.length}`}
            className="max-w-full max-h-full object-contain"
            custom={slideDirection}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: DURATION_NORMAL / 1000 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="fullscreen-photo"
          />
        </AnimatePresence>

        {/* Navigation arrows in fullscreen */}
        {hasMultiplePhotos && <NavigationArrows className="lg:opacity-70" />}

        {/* Photo counter in fullscreen */}
        {hasMultiplePhotos && <PhotoCounter />}
      </motion.div>,
      document.body
    );
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`relative ${className}`}
        role="group"
        aria-label={`Photo gallery showing image ${currentIndex + 1} of ${photos.length}`}
        data-testid="photo-gallery"
        tabIndex={hasMultiplePhotos ? 0 : undefined}
        onTouchStart={hasMultiplePhotos ? handleTouchStart : undefined}
        onTouchEnd={hasMultiplePhotos ? handleTouchEnd : undefined}
      >
        {/* Loading placeholder - gradient background (Issue 1: skip if preloaded) */}
        {isImageLoading && !isPreloaded && (
          <div
            className="absolute inset-0 bg-gradient-to-br from-bg-base to-bg-surface animate-pulse rounded-lg"
            aria-hidden="true"
            data-testid="loading-placeholder"
          />
        )}

        {/* Error placeholder (Issue 5: Russian text) */}
        {hasError && (
          <div className="absolute inset-0 bg-bg-surface flex items-center justify-center rounded-lg">
            <span className="text-gray-500 text-sm">Failed to load image</span>
          </div>
        )}

        {/* Photo with slide animation */}
        <AnimatePresence mode="wait" custom={slideDirection}>
          <motion.img
            key={currentPhoto}
            src={currentPhoto}
            alt={`Photo ${currentIndex + 1} of ${photos.length}`}
            className={`w-full aspect-[4/3] object-cover rounded-lg cursor-pointer ${
              isImageLoading && !isPreloaded ? 'opacity-0' : 'opacity-100'
            }`}
            custom={slideDirection}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: DURATION_NORMAL / 1000 }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onClick={handleOpenFullscreen}
            data-testid="gallery-photo"
          />
        </AnimatePresence>

        {/* Navigation arrows - only for multi-photo places */}
        {hasMultiplePhotos && <NavigationArrows className="lg:opacity-0 lg:group-hover:opacity-70" />}

        {/* Photo count indicator - only for multi-photo places */}
        {/* Issue 2: Disable aria-live when fullscreen is active to prevent duplicate announcements */}
        {hasMultiplePhotos && (
          <div
            className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded-full text-xs text-white"
            aria-live={isFullscreen ? undefined : "polite"}
            data-testid="photo-counter"
          >
            {currentIndex + 1}/{photos.length}
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      <AnimatePresence>
        <FullscreenOverlay />
      </AnimatePresence>
    </>
  );
}
